import {z} from 'zod';

import {localeSchema} from '@dms/api/core';

// Maintained by hand from the draft contract financing-ending.openapi.yaml 0.3.10.

// Server-owned vocabularies are open strings so that a value added on the server still parses.
export const FinancingEndingSkipReasonCodeSchema = z.string();
export type FinancingEndingSkipReasonCode = z.infer<typeof FinancingEndingSkipReasonCodeSchema>;

export const FinancingEndingRecipientSourceSchema = z.string();
export type FinancingEndingRecipientSource = z.infer<typeof FinancingEndingRecipientSourceSchema>;

export const FinancingEndingPreflightBlockReasonSchema = z.string();
export type FinancingEndingPreflightBlockReason = z.infer<
  typeof FinancingEndingPreflightBlockReasonSchema
>;

export const FinancingEndingBulkOperationStateSchema = z.string();
export type FinancingEndingBulkOperationState = z.infer<
  typeof FinancingEndingBulkOperationStateSchema
>;

export const FinancingEndingBulkOperationItemStateSchema = z.string();
export type FinancingEndingBulkOperationItemState = z.infer<
  typeof FinancingEndingBulkOperationItemStateSchema
>;

export const FinancingEndingEmailOutcomeSchema = z.string();
export type FinancingEndingEmailOutcome = z.infer<typeof FinancingEndingEmailOutcomeSchema>;

// E2 POST /dms/v1/financing-ending/email/preflight
export const FinancingEndingPreflightRequestBodySchema = z.object({
  rowIds: z.array(z.string()).min(1).max(500),
});
export type FinancingEndingPreflightRequestBody = z.infer<
  typeof FinancingEndingPreflightRequestBodySchema
>;

export const FinancingEndingPreflightRowResponseBodySchema = z.object({
  rowId: z.string(),
  eligible: z.boolean(),
  reasonCodes: z.array(FinancingEndingSkipReasonCodeSchema),
  recipientSource: FinancingEndingRecipientSourceSchema.nullable(),
  recipientName: z.string().nullable(),
  recipientEmail: z.string().nullable(),
});
export type FinancingEndingPreflightRowResponseBody = z.infer<
  typeof FinancingEndingPreflightRowResponseBodySchema
>;

export const FinancingEndingPreflightResponseBodySchema = z.object({
  selectedCount: z.number(),
  outreachCandidateRows: z.number(),
  plannedCount: z.number(),
  skippedCount: z.number(),
  cap: z.number(),
  canSend: z.boolean(),
  blockReason: FinancingEndingPreflightBlockReasonSchema.nullable(),
  rows: z.array(FinancingEndingPreflightRowResponseBodySchema),
  defaultEmailCredentialsId: z.string().nullable(),
  canManageTemplates: z.boolean(),
});
export type FinancingEndingPreflightResponseBody = z.infer<
  typeof FinancingEndingPreflightResponseBodySchema
>;

export type PreflightFinancingEndingEmailApiArg = {
  financingEndingPreflightRequestBody: FinancingEndingPreflightRequestBody;
};
export type PreflightFinancingEndingEmailApiResponse = FinancingEndingPreflightResponseBody;

// E3 POST /dms/v1/financing-ending/email/preview
// A property the caller does not send must be left out: the server refuses null in every optional field.
// The written subject and body are the source; templateId only gives the locale when locale is left out.
export const FinancingEndingPreviewRequestBodySchema = z
  .object({
    rowId: z.string(),
    emailCredentialsId: z.string(),
    subject: z.string(),
    body: z.string(),
    templateId: z.string().optional(),
    locale: localeSchema.optional(),
  })
  .strict();
export type FinancingEndingPreviewRequestBody = z.infer<
  typeof FinancingEndingPreviewRequestBodySchema
>;

export const FinancingEndingPreviewResponseBodySchema = z.object({
  rowId: z.string(),
  recipientEmail: z.string(),
  subject: z.string(),
  bodyHtml: z.string(),
  missingPlaceholders: z.array(z.string()),
});
export type FinancingEndingPreviewResponseBody = z.infer<
  typeof FinancingEndingPreviewResponseBodySchema
>;

export type PreviewEditableFinancingEndingEmailApiArg = {
  financingEndingPreviewRequestBody: FinancingEndingPreviewRequestBody;
};
export type PreviewEditableFinancingEndingEmailApiResponse = FinancingEndingPreviewResponseBody;

// POST /dms/v1/financing-ending/email/source — 201; every request stores a new source.
export const FinancingEndingEmailSourceRequestBodySchema = z
  .object({
    subject: z.string(),
    body: z.string(),
    templateId: z.string().optional(),
    locale: localeSchema.optional(),
  })
  .strict();
export type FinancingEndingEmailSourceRequestBody = z.infer<
  typeof FinancingEndingEmailSourceRequestBodySchema
>;

export const FinancingEndingEmailSourceResponseBodySchema = z.object({
  sourceId: z.string(),
  locale: localeSchema,
});
export type FinancingEndingEmailSourceResponseBody = z.infer<
  typeof FinancingEndingEmailSourceResponseBodySchema
>;

export type PrepareFinancingEndingEmailSourceApiArg = {
  financingEndingEmailSourceRequestBody: FinancingEndingEmailSourceRequestBody;
};
export type PrepareFinancingEndingEmailSourceApiResponse = FinancingEndingEmailSourceResponseBody;

// E5 POST /dms/v1/bulk-operation/resource/BUSINESS_CASE/action/{actionCode}
// The params are kept as references only: the written text goes to the source, never into the params.
export const FinancingEndingEmailBulkActionParamsSchema = z
  .object({
    sourceId: z.string(),
    // The template the text was prefilled from, kept for provenance only.
    templateId: z.string().optional(),
    emailCredentialsId: z.string(),
    attachmentFileIds: z.array(z.string()),
    // Subset of attachmentFileIds that the server converts to PDF before sending.
    pdfAttachmentFileIds: z.array(z.string()).default([]),
  })
  .strict();
export type FinancingEndingEmailBulkActionParams = z.infer<
  typeof FinancingEndingEmailBulkActionParamsSchema
>;

export const FinancingEndingSubmitBulkActionRequestBodySchema = z.object({
  recordIds: z.array(z.string()),
  params: FinancingEndingEmailBulkActionParamsSchema,
});
export type FinancingEndingSubmitBulkActionRequestBody = z.infer<
  typeof FinancingEndingSubmitBulkActionRequestBodySchema
>;

export const FinancingEndingSubmitBulkActionResponseBodySchema = z.object({
  bulkOperationId: z.string(),
});
export type FinancingEndingSubmitBulkActionResponseBody = z.infer<
  typeof FinancingEndingSubmitBulkActionResponseBodySchema
>;

export type SubmitFinancingEndingBulkActionApiArg = {
  actionCode: string;
  submitBulkActionRequestBody: FinancingEndingSubmitBulkActionRequestBody;
};
export type SubmitFinancingEndingBulkActionApiResponse =
  FinancingEndingSubmitBulkActionResponseBody;

// E6 GET /dms/v1/bulk-operation/{bulkOperationId}
export const FinancingEndingMailboxSchema = z.object({
  label: z.string().nullable(),
  address: z.string().nullable(),
});
export type FinancingEndingMailbox = z.infer<typeof FinancingEndingMailboxSchema>;

// The neutral bulk-operation response: the action that ran defines the outcome codes and the detail.
// A financing-ending operation counts every item outcome code, with 0 when no item has it.
export const FinancingEndingOutcomeCountsWireSchema = z.object({
  SENT: z.number(),
  FAILED: z.number(),
  BLOCKED: z.number(),
  PENDING: z.number(),
});
export type FinancingEndingOutcomeCountsWire = z.infer<
  typeof FinancingEndingOutcomeCountsWireSchema
>;

export const FinancingEndingOperationDetailWireSchema = z.object({
  templateName: z.string().nullable(),
  mailbox: FinancingEndingMailboxSchema.nullable(),
  sentBy: z.string().nullable(),
});
export type FinancingEndingOperationDetailWire = z.infer<
  typeof FinancingEndingOperationDetailWireSchema
>;

export const FinancingEndingBulkOperationWireSchema = z.object({
  bulkOperationId: z.string(),
  resourceId: z.string(),
  actionCode: z.string(),
  state: FinancingEndingBulkOperationStateSchema,
  totalCount: z.number(),
  doneCount: z.number(),
  failedCount: z.number(),
  skippedCount: z.number(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  // Null in the listing and for an action without item detail.
  outcomeCounts: FinancingEndingOutcomeCountsWireSchema.nullable(),
  detail: FinancingEndingOperationDetailWireSchema.nullable(),
});
export type FinancingEndingBulkOperationWire = z.infer<
  typeof FinancingEndingBulkOperationWireSchema
>;

// The e-mail outcomes of all items of the operation.
export type FinancingEndingEmailOutcomeCounts = {
  sent: number;
  failed: number;
  blocked: number;
  pending: number;
};

// The operation as the client reads it: the financing-ending detail is flattened.
export type FinancingEndingBulkOperationResponseBody = Omit<
  FinancingEndingBulkOperationWire,
  'outcomeCounts' | 'detail'
> & {
  templateName?: string | null;
  mailbox?: FinancingEndingMailbox | null;
  sentBy?: string | null;
  emailOutcomeCounts?: FinancingEndingEmailOutcomeCounts | null;
};

export type GetFinancingEndingBulkOperationApiArg = {
  bulkOperationId: string;
};
export type GetFinancingEndingBulkOperationApiResponse = FinancingEndingBulkOperationResponseBody;

// E7 GET /dms/v1/bulk-operation/{bulkOperationId}/item — plain array, no total
// Present for a DONE item only, and then always with the id of the communication in the message history;
// the client reads sentMessageId only as the presence of the message-history link.
export const FinancingEndingItemDetailWireSchema = z.object({
  recipientName: z.string().nullable(),
  recipientEmail: z.string().nullable(),
  sentMessageId: z.string(),
});
export type FinancingEndingItemDetailWire = z.infer<typeof FinancingEndingItemDetailWireSchema>;

export const FinancingEndingBulkOperationItemWireSchema = z.object({
  recordId: z.string(),
  recordLabel: z.string().nullable(),
  recordAvailable: z.boolean(),
  state: FinancingEndingBulkOperationItemStateSchema,
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  errorParams: z.record(z.string(), z.string()).nullable(),
  processedAt: z.string().nullable(),
  outcome: FinancingEndingEmailOutcomeSchema.nullable(),
  detail: FinancingEndingItemDetailWireSchema.nullable(),
});
export type FinancingEndingBulkOperationItemWire = z.infer<
  typeof FinancingEndingBulkOperationItemWireSchema
>;

export const ListFinancingEndingBulkOperationItemsWireSchema = z.array(
  FinancingEndingBulkOperationItemWireSchema
);
export type ListFinancingEndingBulkOperationItemsWire = z.infer<
  typeof ListFinancingEndingBulkOperationItemsWireSchema
>;

// The item as the client reads it: the outcome and the detail are flattened. The recipient is null
// for an item without a detail, and the name and e-mail also when the customer is not readable.
export type FinancingEndingBulkOperationItemResponseBody = Omit<
  FinancingEndingBulkOperationItemWire,
  'outcome' | 'detail'
> & {
  emailOutcome?: FinancingEndingEmailOutcome | null;
  recipientName?: string | null;
  recipientEmail?: string | null;
  sentMessageId?: string | null;
};

export type ListFinancingEndingBulkOperationItemsApiResponse =
  FinancingEndingBulkOperationItemResponseBody[];

export type ListFinancingEndingBulkOperationItemsApiArg = {
  bulkOperationId: string;
  limit?: number;
  offset?: number;
  state?: FinancingEndingBulkOperationItemState;
};

// E8 POST /dms/v1/bulk-operation/{bulkOperationId}/rerun-failed — 202 with a new id, 204 (null) when nothing failed
export const RerunFailedFinancingEndingBulkOperationApiResponseSchema =
  FinancingEndingSubmitBulkActionResponseBodySchema.nullable();
export type RerunFailedFinancingEndingBulkOperationApiResponse = z.infer<
  typeof RerunFailedFinancingEndingBulkOperationApiResponseSchema
>;

export type RerunFailedFinancingEndingBulkOperationApiArg = {
  bulkOperationId: string;
};
